import React, { useMemo } from 'react';

import pluralize from 'pluralize';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/pro-solid-svg-icons';

import { Card } from '@buildoutinc/blueprint-react/ui/Card';
import { ContactsCardEmploymentGroup } from './employment_group';
import { Empty } from '@buildoutinc/blueprint-react/ui/Empty';
import { groupByEmployment } from './helpers';

import type { Contact, ContactRole } from 'bundles/backoffice/contacts/types';

import './styles.scss';

interface Props {
  features: Record<string, boolean>;
  allowContactEditing?: boolean;
  confirmDeletion?: boolean;
  contactLabel?: string;
  contactRoles: ContactRole[];
  forRecord?: { id: number, type: string };
  header?: React.ReactNode;
  headerControls?: React.ReactNode;
  inline?: boolean;
  quickbooksStatus?: 'customer' | 'vendor';
  readOnly?: boolean;
  required?: boolean;
  subheader?: React.ReactNode;
  onChangeContactRole?: (contactRole: ContactRole, changes: Partial<ContactRole>) => void;
  onRemoveContactRole: (contactRole: ContactRole) => void;
  onSaveContact?: (contact: Contact) => void;
}

// A card for contacts associated with a record
export function ContactRolesCard({
  allowContactEditing = true,
  confirmDeletion,
  contactLabel = 'contact',
  contactRoles,
  features,
  forRecord,
  header = 'Contacts',
  headerControls,
  inline,
  quickbooksStatus,
  readOnly,
  required,
  subheader,
  onChangeContactRole,
  onRemoveContactRole,
  onSaveContact,
}: Props) {
  const employmentGroups = useMemo(
    () => groupByEmployment(contactRoles),
    [contactRoles],
  );

  return (
    <Card>
      <Card.Header className="d-flex align-items-center gap-2">
        <div className="d-flex align-items-end gap-2 flex-fill mb-0">
          <h6>{header}{required && <span className="text-danger">*</span>}</h6>
          <div className="fs-body text-muted">{subheader}</div>
        </div>

        {headerControls}
      </Card.Header>

      <Card.Body className="h-auto overflow-y-auto" style={{ maxHeight: 480 }}>
        {employmentGroups.length === 0 &&
          <Empty className="bg-white h-100" variant={inline ? 'inline' : null}>
            <Empty.Media>
              <FontAwesomeIcon icon={faUser} />
            </Empty.Media>
            <Empty.Title>{`No ${pluralize(contactLabel)} have been added.`}</Empty.Title>
          </Empty>
        }

        {employmentGroups.map((group, i) => (
          <ContactsCardEmploymentGroup
            allowContactEditing={allowContactEditing}
            confirmDeletion={confirmDeletion}
            features={features}
            forRecord={forRecord}
            group={group}
            index={i}
            key={i}
            quickbooksStatus={quickbooksStatus}
            readOnly={readOnly}
            onChangeContactRole={onChangeContactRole}
            onRemoveContactRole={onRemoveContactRole}
            onSaveContact={onSaveContact}
          />
        ))}
      </Card.Body>
    </Card>
  );
}
